import { MARKET_DATA_DATABENTO_RECENT_TAIL_MESSAGE } from "../constants/marketDataDisclaimers";
import { isFuturesOrdersCsvBroker } from "./futuresOrdersCsvBroker";
import { getIanaTimezoneOffsetMinutes, normalizeIanaTimeZone } from "./ianaTimezones";

/** Databento pay-as-you-go historical CME bars typically lag ~48h. */
export const DEMO_DATABENTO_RECENT_TAIL_MS = 48 * 60 * 60 * 1000;

export const DEMO_FUTURES_RECENT_TAIL_COPY =
  "US futures market data usually excludes the most recent ~48 hours. " +
  "Your Orders file includes fills in that window (using your Fill Time timezone), so this demo report can’t run yet. " +
  "Re-export Orders ending before that window, or wait and try again.";

export type DemoFuturesRecentTailScan = {
  latestFillUtcMs: number | null;
  fillCount: number;
  scanned: boolean;
};

type WallParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

function parseCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
}

function detectDelimiter(headerLine: string): string {
  const tabCount = (headerLine.match(/\t/g) ?? []).length;
  const semiCount = (headerLine.match(/;/g) ?? []).length;
  const commaCount = (headerLine.match(/,/g) ?? []).length;
  if (tabCount > 0 && tabCount >= semiCount && tabCount >= commaCount) return "\t";
  if (semiCount > 0 && semiCount > commaCount) return ";";
  return ",";
}

function normalizeHeader(header: string): string {
  return header.replace(/^"|"$/g, "").trim().toLowerCase().replace(/\s+/g, " ");
}

function findColumnIndex(headers: string[], candidates: string[]): number {
  const normalized = headers.map(normalizeHeader);
  for (const candidate of candidates) {
    const idx = normalized.indexOf(candidate);
    if (idx >= 0) return idx;
  }
  return -1;
}

/** Convert wall-clock components in `timeZone` to a UTC epoch ms. */
export function wallTimeInZoneToUtcMs(parts: WallParts, timeZone: string): number {
  const zone = normalizeIanaTimeZone(timeZone);
  const asUtcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const offset1 = getIanaTimezoneOffsetMinutes(zone, new Date(asUtcGuess));
  let utcMs = asUtcGuess - offset1 * 60_000;
  const offset2 = getIanaTimezoneOffsetMinutes(zone, new Date(utcMs));
  if (offset2 !== offset1) {
    utcMs = asUtcGuess - offset2 * 60_000;
  }
  return utcMs;
}

function parseAmPmHour(hour12: number, meridiem: string): number | null {
  if (!Number.isFinite(hour12) || hour12 < 1 || hour12 > 12) return null;
  const m = meridiem.trim().toUpperCase();
  if (m === "AM") return hour12 === 12 ? 0 : hour12;
  if (m === "PM") return hour12 === 12 ? 12 : hour12 + 12;
  return null;
}

/** Parse Tradovate / NinjaTrader Orders "Fill Time" cell into wall-clock parts. */
export function parseFuturesOrdersFillTimeParts(raw: string): WallParts | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // 9/26/2025 3:45:12 PM  |  09/26/2025 15:45:12
  const us = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i
  );
  if (us) {
    const month = Number(us[1]);
    const day = Number(us[2]);
    const year = Number(us[3]);
    let hour = Number(us[4]);
    const minute = Number(us[5]);
    const second = Number(us[6] ?? "0");
    if (us[7]) {
      const converted = parseAmPmHour(hour, us[7]);
      if (converted == null) return null;
      hour = converted;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23) return null;
    return { year, month, day, hour, minute, second };
  }

  // 2025-09-26 15:45:12  |  2025-09-26T15:45:12
  const isoLocal = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (isoLocal && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    return {
      year: Number(isoLocal[1]),
      month: Number(isoLocal[2]),
      day: Number(isoLocal[3]),
      hour: Number(isoLocal[4]),
      minute: Number(isoLocal[5]),
      second: Number(isoLocal[6] ?? "0"),
    };
  }

  return null;
}

/**
 * Interpret a Fill Time cell as UTC ms using the import IANA timezone
 * (or absolute instant when the cell already carries Z/offset).
 */
export function fillTimeCellToUtcMs(raw: string, timeZone: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed) && (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed))) {
    const ms = Date.parse(trimmed);
    return Number.isFinite(ms) ? ms : null;
  }

  const parts = parseFuturesOrdersFillTimeParts(trimmed);
  if (!parts) return null;
  return wallTimeInZoneToUtcMs(parts, timeZone);
}

/**
 * Scan an Orders CSV for the newest Fill Time (Filled rows when Status is present).
 */
export function scanFuturesOrdersCsvLatestFillUtcMs(
  csvText: string,
  timeZone: string
): DemoFuturesRecentTailScan {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (!lines.length) return { latestFillUtcMs: null, fillCount: 0, scanned: true };

  let headerIndex = 0;
  if (/^sep=/i.test(lines[0].trim())) headerIndex = 1;
  const headerLine = lines[headerIndex];
  if (!headerLine?.trim()) return { latestFillUtcMs: null, fillCount: 0, scanned: true };

  const delimiter = detectDelimiter(headerLine);
  const headers = parseCsvLine(headerLine, delimiter);
  const fillIdx = findColumnIndex(headers, ["fill time", "filltime", "fill_time"]);
  if (fillIdx < 0) return { latestFillUtcMs: null, fillCount: 0, scanned: true };

  const statusIdx = findColumnIndex(headers, ["status"]);
  let latestFillUtcMs: number | null = null;
  let fillCount = 0;

  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i], delimiter);
    if (!cells.some((c) => c.trim())) continue;
    if (statusIdx >= 0) {
      const status = normalizeHeader(cells[statusIdx] ?? "");
      if (status && status !== "filled") continue;
    }
    const ms = fillTimeCellToUtcMs(cells[fillIdx] ?? "", timeZone);
    if (ms == null) continue;
    fillCount += 1;
    if (latestFillUtcMs == null || ms > latestFillUtcMs) latestFillUtcMs = ms;
  }

  return { latestFillUtcMs, fillCount, scanned: true };
}

export async function scanFuturesOrdersFileLatestFillUtcMs(
  file: File,
  timeZone: string
): Promise<DemoFuturesRecentTailScan> {
  try {
    const text = await file.text();
    return scanFuturesOrdersCsvLatestFillUtcMs(text, timeZone);
  } catch {
    return { latestFillUtcMs: null, fillCount: 0, scanned: false };
  }
}

export function isWithinDatabentoRecentTail(
  latestFillUtcMs: number,
  nowMs: number = Date.now(),
  tailMs: number = DEMO_DATABENTO_RECENT_TAIL_MS
): boolean {
  return latestFillUtcMs > nowMs - tailMs;
}

/**
 * Demo Generate gate: NinjaTrader / Tradovate Orders with fills inside Databento’s ~48h lag.
 * Returns user-facing copy when blocked; null when OK or when we lack fill timestamps.
 */
export function demoFuturesRecentTailBlockedMessage(input: {
  brokerName?: string | null;
  latestFillUtcMs?: number | null;
  nowMs?: number;
}): string | null {
  if (!isFuturesOrdersCsvBroker(input.brokerName)) return null;
  const latest = input.latestFillUtcMs;
  if (latest == null || !Number.isFinite(latest)) return null;
  if (!isWithinDatabentoRecentTail(latest, input.nowMs ?? Date.now())) return null;
  return DEMO_FUTURES_RECENT_TAIL_COPY;
}

/** Soft chart copy reused elsewhere — keep export for consistency. */
export function demoFuturesRecentTailChartHint(): string {
  return MARKET_DATA_DATABENTO_RECENT_TAIL_MESSAGE;
}
